import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// API 기본 설정
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api/cms';

// Axios 인스턴스 생성
const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 토큰 관리
export const tokenManager = {
  getAccessToken: (): string | null => {
    return localStorage.getItem('accessToken');
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem('refreshToken');
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  },

  clearTokens: (): void => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  getUser: (): any => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  setUser: (user: any): void => {
    localStorage.setItem('user', JSON.stringify(user));
  }
};

// 요청 인터셉터: 토큰 자동 첨부
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 토큰 만료 처리
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 로그인 요청은 인터셉터에서 처리하지 않음
    if (originalRequest.url?.includes('/auth/login')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}${API_PREFIX}/auth/refresh-token`, {
            refreshToken
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          tokenManager.setTokens(accessToken, newRefreshToken);

          // 원래 요청 재시도
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // 리프레시 실패 시 로그아웃
          tokenManager.clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // 리프레시 토큰이 없으면 로그인 페이지로
        tokenManager.clearTokens();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// 범용 API 요청 함수
export const apiRequest = async (
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  data?: any,
  config?: any
): Promise<any> => {
  try {
    const response = await api({
      method,
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error) {
    console.error(`API request failed: ${method} ${url}`, error);
    throw error;
  }
};

// 스케줄 API 함수들
export const scheduleAPI = {
  // 스케줄 목록 조회
  getSchedules: async (scheduleType?: string) => {
    const params = scheduleType ? { scheduleType } : {};
    return apiRequest('GET', '/schedules', undefined, { params });
  },

  // 스케줄 상세 조회
  getSchedule: async (id: number) => {
    return apiRequest('GET', `/schedules/${id}`);
  },

  // 스케줄 생성
  createSchedule: async (data: {
    scheduleType: string;
    timeSlotName: string;
    startTime: string;
    endTime: string;
    isActive?: boolean;
    contents?: Array<{
      contentTitle: string;
      displayOrder?: number;
      duration?: string;
    }>;
  }) => {
    return apiRequest('POST', '/schedules', data);
  },

  // 스케줄 수정
  updateSchedule: async (id: number, data: {
    scheduleType?: string;
    timeSlotName?: string;
    startTime?: string;
    endTime?: string;
    isActive?: boolean;
  }) => {
    return apiRequest('PUT', `/schedules/${id}`, data);
  },

  // 스케줄 삭제
  deleteSchedule: async (id: number) => {
    return apiRequest('DELETE', `/schedules/${id}`);
  },

  // 스케줄 콘텐츠 수정
  updateScheduleContents: async (id: number, contents: Array<{
    contentItemId: number;
    displayOrder: number;
    duration: string;
  }>) => {
    return apiRequest('PUT', `/schedules/${id}/contents`, { contents });
  },

  // 스케줄 활성화/비활성화
  toggleSchedule: async (id: number) => {
    return apiRequest('PATCH', `/schedules/${id}/toggle`);
  },

  // 스케줄 타입 순서 조회
  getScheduleTypesOrder: async () => {
    return apiRequest('GET', '/schedules/types-order');
  },

  // 스케줄 타입 순서 저장
  saveScheduleTypesOrder: async (scheduleTypesOrder: string[]) => {
    return apiRequest('PUT', '/schedules/types-order', { scheduleTypesOrder });
  },

  // 실시간 스케줄 타입 조회
  getActiveRealtimeSchedule: async () => {
    return apiRequest('GET', '/schedules/active-realtime');
  },

  // 실시간 스케줄 타입 설정
  setActiveRealtimeSchedule: async (scheduleType: string) => {
    return apiRequest('PUT', '/schedules/active-realtime', { scheduleType });
  }
};

export default api;